import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Save, RefreshCcw, Download, FileText, Loader2, AlertCircle,
  Edit3, Check, ChevronDown, ChevronRight, Maximize, Minimize,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getStoredToken } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Schema-driven section config ==============
const NUMBER_FIELDS = {
  annulus: [
    ["diameter_min_mm", "Min Ø", "mm"],
    ["diameter_max_mm", "Max Ø", "mm"],
    ["diameter_mean_mm", "Mean Ø", "mm"],
    ["perimeter_mm", "Perimeter", "mm"],
    ["perimeter_derived_diameter_mm", "Perimeter-derived Ø", "mm"],
    ["area_mm2", "Area", "mm²"],
    ["area_derived_diameter_mm", "Area-derived Ø", "mm"],
  ],
  lvot: [
    ["diameter_min_mm", "Min Ø", "mm"],
    ["diameter_max_mm", "Max Ø", "mm"],
    ["diameter_mean_mm", "Mean Ø", "mm"],
    ["perimeter_mm", "Perimeter", "mm"],
    ["perimeter_derived_diameter_mm", "Perimeter-derived Ø", "mm"],
    ["area_mm2", "Area", "mm²"],
    ["area_derived_diameter_mm", "Area-derived Ø", "mm"],
  ],
  sinus_of_valsalva: [
    ["lcc_diameter_mm", "LCC Ø", "mm"],
    ["rcc_diameter_mm", "RCC Ø", "mm"],
    ["ncc_diameter_mm", "NCC Ø", "mm"],
    ["lcc_height_mm", "LCC height", "mm"],
    ["rcc_height_mm", "RCC height", "mm"],
    ["ncc_height_mm", "NCC height", "mm"],
  ],
  coronary_ostia: [
    ["left_height_mm", "Left ostium height", "mm"],
    ["right_height_mm", "Right ostium height", "mm"],
  ],
  ascending_aorta: [
    ["min_diameter_mm", "Min Ø", "mm"],
    ["max_diameter_mm", "Max Ø", "mm"],
    ["avg_diameter_mm", "Avg Ø", "mm"],
  ],
  sinotubular_junction: [
    ["min_diameter_mm", "Min Ø", "mm"],
    ["max_diameter_mm", "Max Ø", "mm"],
  ],
  femoral_access_right: [
    ["common_iliac_min_mm", "CIA Min", "mm"],
    ["common_iliac_max_mm", "CIA Max", "mm"],
    ["common_iliac_avg_mm", "CIA Avg", "mm"],
    ["external_iliac_min_mm", "EIA Min", "mm"],
    ["external_iliac_max_mm", "EIA Max", "mm"],
    ["external_iliac_avg_mm", "EIA Avg", "mm"],
    ["femoral_min_mm", "Femoral Min", "mm"],
    ["femoral_max_mm", "Femoral Max", "mm"],
    ["femoral_avg_mm", "Femoral Avg", "mm"],
  ],
  femoral_access_left: [
    ["common_iliac_min_mm", "CIA Min", "mm"],
    ["common_iliac_max_mm", "CIA Max", "mm"],
    ["common_iliac_avg_mm", "CIA Avg", "mm"],
    ["external_iliac_min_mm", "EIA Min", "mm"],
    ["external_iliac_max_mm", "EIA Max", "mm"],
    ["external_iliac_avg_mm", "EIA Avg", "mm"],
    ["femoral_min_mm", "Femoral Min", "mm"],
    ["femoral_max_mm", "Femoral Max", "mm"],
    ["femoral_avg_mm", "Femoral Avg", "mm"],
  ],
  valve_selection: [
    ["recommended_size_mm", "Recommended size", "mm"],
    ["annulus_diameter_a_mm", "Annulus Ø (A)", "mm"],
    ["annulus_perimeter_mm", "Annulus perimeter", "mm"],
    ["sov_diameter_mean_b_mm", "SOV mean Ø (B)", "mm"],
    ["sov_height_mean_c_mm", "SOV mean height (C)", "mm"],
    ["oversizing_percentage", "Oversizing", "%"],
  ],
};

const SECTION_TITLES = {
  annulus: "Annulus",
  lvot: "Left Ventricular Outflow Tract (LVOT)",
  sinus_of_valsalva: "Sinus of Valsalva",
  coronary_ostia: "Coronary Ostia",
  ascending_aorta: "Ascending Aorta",
  sinotubular_junction: "Sinotubular Junction",
  femoral_access_right: "Femoral Access — Right",
  femoral_access_left: "Femoral Access — Left",
  valve_selection: "Valve Selection (Evolut FX+)",
};

// ============== Reusable bits ==============
function NumberCell({ value, unit, editing, onChange, testId }) {
  if (!editing) {
    return (
      <span className="text-sm font-medium" data-testid={testId}>
        {value === null || value === undefined || value === "" ? "—" : value}
        {value !== null && value !== undefined && value !== "" && unit ? (
          <span className="text-xs text-muted-foreground ml-1">{unit}</span>
        ) : null}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step="0.1"
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? null : Number(raw));
        }}
        className="h-7 text-sm w-24"
        data-testid={testId}
      />
      <span className="text-xs text-muted-foreground">{unit}</span>
    </div>
  );
}

function MeasurementSection({ id, fields, data, editing, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(true);
  if (!data) return null;
  return (
    <div className="border border-border rounded-lg" data-testid={`section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/40"
      >
        <div className="text-sm font-medium">
          {SECTION_TITLES[id] || id}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
          {fields.map(([key, label, unit]) => (
            <div key={key} className="space-y-1">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
              <NumberCell
                value={data[key]}
                unit={unit}
                editing={editing}
                onChange={(v) => onChange(id, key, v)}
                testId={`${testIdPrefix}-${id}-${key}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StringField({ label, value, editing, onChange, testId, multiline = false }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</Label>
      {editing ? (
        multiline ? (
          <Textarea
            rows={3}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
        ) : (
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 text-sm"
            data-testid={testId}
          />
        )
      ) : (
        <div className="text-sm" data-testid={testId}>{value || <span className="text-muted-foreground">—</span>}</div>
      )}
    </div>
  );
}

// ============== Page ==============
export default function ReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(searchParams.get("edit") === "1");
  const [saving, setSaving] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const pdfPaneRef = useRef(null);

  const handleBack = useCallback(() => {
    const from = location.state?.from;
    if (typeof from === "string" && from.startsWith("/")) {
      navigate(from);
      return;
    }
    // Prefer browser history (patient studies, reports list, studies page, etc.).
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/reports");
  }, [location.state?.from, navigate]);

  const handleTogglePdfFullscreen = useCallback(async () => {
    const el = pdfPaneRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (e) {
      console.warn("PDF fullscreen failed:", e);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setPdfFullscreen(document.fullscreenElement === pdfPaneRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const pdfUrl = useMemo(() => {
    const tok = getStoredToken();
    return `${API}/reports/${reportId}/file${tok ? `#token=${tok}` : ""}`;
    // Note: we attach the token via axios default header for the PDF iframe via a blob URL below.
  }, [reportId]);

  // Fetch the PDF as blob (so iframe gets it with auth) and the report doc.
  const [blobUrl, setBlobUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/${reportId}`);
      setReport(r.data);
      setDraft(r.data.extracted || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reportId]);

  useEffect(() => {
    let revoked = false;
    let url = null;
    (async () => {
      try {
        const r = await axios.get(`${API}/reports/${reportId}/file`, { responseType: "blob" });
        if (revoked) return;
        // Force application/pdf so browsers render via PDF.js even if Blob
        // lost its content-type going through axios.
        const pdfBlob = new Blob([r.data], { type: "application/pdf" });
        url = URL.createObjectURL(pdfBlob);
        setBlobUrl(url);
      } catch (e) {
        console.error("PDF preview load failed", e);
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reportId]);

  const updateField = (section, key, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (section === null) return { ...prev, [key]: value };
      return { ...prev, [section]: { ...(prev[section] || {}), [key]: value } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/reports/${reportId}`, { extracted: draft });
      setReport(r.data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReExtract = async () => {
    if (!confirm("Re-run AI extraction on this PDF? Unsaved edits will be replaced.")) return;
    setReextracting(true);
    try {
      const r = await axios.post(`${API}/reports/${reportId}/re-extract`);
      setReport(r.data);
      setDraft(r.data.extracted || {});
      toast.success("Re-fetched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-fetch failed");
    } finally {
      setReextracting(false);
    }
  };

  if (loading || !report || !draft) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="report-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const ex = draft;
  const failed = report.extract_status === "failed";
  const confidence = typeof report.extracted?.confidence === "number"
    ? `${Math.round(report.extracted.confidence * 100)}%`
    : "—";

  return (
    <div className="space-y-4" data-testid="report-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            data-testid="report-back-btn"
            className="gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <div className="text-sm font-medium">
              {report.original_filename}
            </div>
            <div className="text-xs text-muted-foreground">
              Study <Link to={`/viewer/${report.study_id}`} className="text-[var(--xl-primary)] hover:underline">{report.study_id?.slice(0, 8)}</Link>
              {" · "}Patient <Link to={`/patients/${report.patient_uhid}`} className="text-[var(--xl-primary)] hover:underline">{report.patient_uhid}</Link>
         
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {failed ? (
            <Badge variant="outline" className="text-[10px] text-red-600 border-red-200 gap-1">
              <AlertCircle className="w-3 h-3" /> Extraction failed: {report.extract_error}
            </Badge>
          ) : null}
          <Button variant="outline" size="sm" onClick={handleReExtract} disabled={reextracting} data-testid="report-reextract-btn">
            {reextracting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1" />}
            Re-Fetch
          </Button>
          {editing ? (
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="report-save-btn" className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save changes
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="report-edit-btn">
              <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit fields
            </Button>
          )}
        </div>
      </div>

      {/* Side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-180px)]">
        {/* PDF Preview */}
        <Card
          ref={pdfPaneRef}
          className="overflow-hidden flex flex-col [&:fullscreen]:h-screen [&:fullscreen]:w-full [&:fullscreen]:rounded-none [&:fullscreen]:border-0"
          data-testid="report-pdf-pane"
        >
          <CardHeader className="py-3 flex flex-row items-center justify-between space-y-0 gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" /> Original PDF
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={handleTogglePdfFullscreen}
              aria-label={pdfFullscreen ? "Exit fullscreen" : "View PDF fullscreen"}
              data-testid="report-pdf-fullscreen-btn"
            >
              {pdfFullscreen ? (
                <Minimize className="w-3.5 h-3.5" />
              ) : (
                <Maximize className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">{pdfFullscreen ? "Exit" : "Fullscreen"}</span>
            </Button>
          </CardHeader>
          <Separator />
          <div className="flex-1 min-h-0 bg-secondary/40">
            {blobUrl ? (
              <iframe
                title="Clinical report PDF"
                src={blobUrl}
                className="w-full h-full"
                data-testid="report-pdf-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading PDF…
              </div>
            )}
          </div>
        </Card>

        {/* Extracted fields */}
        <Card className="overflow-hidden flex flex-col" data-testid="report-fields-pane">
          <CardHeader className="py-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm flex items-center gap-2">
              <Check className="w-4 h-4 text-[#0D9488]" /> Extracted fields
            </CardTitle>
            <Badge variant="outline" className="text-[10px]">
              {ex.report_type === "tavr_planning_3mensio" ? "TAVR · 3mensio" : "Cardiac CT"}
            </Badge>
          </CardHeader>
          <Separator />
          <ScrollArea className="flex-1">
            <CardContent className="space-y-5 p-4">
              {/* Patient identity */}
              <section data-testid="section-patient-identity">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Patient Identity</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <StringField label="Name" value={ex.patient_identity?.name}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "name", v)}
                    testId="field-patient-name" />
                  <StringField label="Sex" value={ex.patient_identity?.sex}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "sex", v)}
                    testId="field-patient-sex" />
                  <StringField label="Physician" value={ex.patient_identity?.physician_name}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "physician_name", v)}
                    testId="field-patient-physician" />
                  <StringField label="Hospital" value={ex.patient_identity?.hospital}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "hospital", v)}
                    testId="field-patient-hospital" />
                  <StringField label="City" value={ex.patient_identity?.city}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "city", v)}
                    testId="field-patient-city" />
                  <StringField label="Country" value={ex.patient_identity?.country}
                    editing={editing}
                    onChange={(v) => updateField("patient_identity", "country", v)}
                    testId="field-patient-country" />
                </div>
              </section>

              {/* Study metadata */}
              <section data-testid="section-study-metadata">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Study Metadata</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <StringField label="Received" value={ex.study_metadata?.received_date}
                    editing={editing}
                    onChange={(v) => updateField("study_metadata", "received_date", v)}
                    testId="field-received-date" />
                  <StringField label="Reviewed" value={ex.study_metadata?.reviewed_date}
                    editing={editing}
                    onChange={(v) => updateField("study_metadata", "reviewed_date", v)}
                    testId="field-reviewed-date" />
                  <StringField label="Reviewer" value={ex.study_metadata?.reviewer_name}
                    editing={editing}
                    onChange={(v) => updateField("study_metadata", "reviewer_name", v)}
                    testId="field-reviewer-name" />
                  <StringField label="Software" value={ex.study_metadata?.software_version}
                    editing={editing}
                    onChange={(v) => updateField("study_metadata", "software_version", v)}
                    testId="field-software-version" />
                </div>
              </section>

              <Separator />

              {/* Annular angulation */}
              <section className="grid grid-cols-2 gap-x-6">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Annular angulation</Label>
                  <NumberCell
                    value={ex.annular_angulation_degrees}
                    unit="°"
                    editing={editing}
                    onChange={(v) => updateField(null, "annular_angulation_degrees", v)}
                    testId="field-annular-angulation"
                  />
                </div>
              </section>

              {/* Measurements (collapsible) */}
              {Object.keys(NUMBER_FIELDS).map((sectionId) => (
                <MeasurementSection
                  key={sectionId}
                  id={sectionId}
                  fields={NUMBER_FIELDS[sectionId]}
                  data={ex[sectionId] || {}}
                  editing={editing}
                  onChange={updateField}
                  testIdPrefix="field"
                />
              ))}

              {/* Procedural considerations */}
              <section data-testid="section-procedural">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Procedural Considerations</h3>
                {(ex.procedural_considerations || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : editing ? (
                  <div className="space-y-2">
                    {(ex.procedural_considerations || []).map((note, i) => (
                      <div key={i} className="flex gap-2">
                        <Input
                          value={note}
                          onChange={(e) => {
                            const next = [...(ex.procedural_considerations || [])];
                            next[i] = e.target.value;
                            updateField(null, "procedural_considerations", next);
                          }}
                          data-testid={`field-procedural-${i}`}
                          className="h-8 text-sm"
                        />
                        <Button variant="ghost" size="sm" onClick={() => {
                          const next = [...(ex.procedural_considerations || [])];
                          next.splice(i, 1);
                          updateField(null, "procedural_considerations", next);
                        }}>×</Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => {
                      updateField(null, "procedural_considerations", [...(ex.procedural_considerations || []), ""]);
                    }}>+ Add note</Button>
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {(ex.procedural_considerations || []).map((n, i) => (
                      <li key={i} className="text-sm flex gap-2" data-testid={`procedural-note-${i}`}>
                        <span className="text-[var(--xl-primary)]">•</span>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Views */}
              {(ex.views || []).length > 0 && (
                <section data-testid="section-views">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Views</h3>
                  <div className="space-y-1.5">
                    {(ex.views || []).map((v, i) => (
                      <div key={i} className="text-sm flex justify-between" data-testid={`view-row-${i}`}>
                        <span className="text-muted-foreground">{v?.name || "—"}</span>
                        <span className="font-mono text-xs">{v?.angles || "—"}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Conclusion */}
              <section data-testid="section-conclusion">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Conclusion</h3>
                {editing ? (
                  <Textarea rows={4} value={ex.conclusion || ""}
                    onChange={(e) => updateField(null, "conclusion", e.target.value)}
                    data-testid="field-conclusion" />
                ) : (
                  <p className="text-sm whitespace-pre-wrap" data-testid="conclusion-text">
                    {ex.conclusion || <span className="text-muted-foreground">—</span>}
                  </p>
                )}
              </section>

              {/* Other sections */}
              {ex.other_sections && Object.keys(ex.other_sections).length > 0 && (
                <section data-testid="section-other">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Other</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {Object.entries(ex.other_sections).map(([k, v]) => (
                      <div key={k}>
                        <div className="text-[11px] text-muted-foreground">{k}</div>
                        <div className="text-sm">{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
